import React, { useEffect, useCallback, useState,useRef } from 'react';
import { listCategories, createProduct, searchVendors, searchCategories, getCurrentUserRole } from '../../services/shipturtleApi';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { hasBlobUrls } from '../../utils/helpers';
import { buildOptionValuesFromCombo, getOptionValue } from '../../utils/optionUtils';
import './ManageProducts.css';
import HTMLEditor from './HTMLEditor';
import OptionNameCombobox from './OptionNameCombobox';
import { productsPath, useProductsChildRoute } from './productsNavigation';

function ManualAddProductPage() {
  const navigate = useNavigate();
  const { closeChild: closeForm, isCurrentChild } = useProductsChildRoute('add');
  // After a successful create, swap the form's history entry for the list the
  // product landed in, so Back doesn't return to a submitted form. Skipped if
  // the user already navigated away while the success message was showing.
  const leaveFormFor = (section) => {
    if (isCurrentChild('manual')) navigate(productsPath(section), { replace: true });
  };
  // Get user role from redux (vendor/admin checks)
  const userTypeRaw = useSelector(state => state.user?.role || state.user?.user_type || null);
  const [userRole, setUserRole] = useState(null);
  
  // Debug logging
  useEffect(() => {
    console.log('ManualAddProductPage mounted');
    console.log('Current userRole:', userRole);
    console.log('Redux userTypeRaw:', userTypeRaw);
  }, [userRole, userTypeRaw]);
  
  // Fetch user role from backend on mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const roleData = await getCurrentUserRole();
        console.log('User role from backend:', roleData);
        setUserRole(roleData.role);
      } catch (err) {
        console.error('Error fetching user role:', err);
        // Fallback to Redux state
        setUserRole(userTypeRaw);
      }
    };
    fetchUserRole();
  }, [userTypeRaw]);
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [variantsOptions, setVariantsOptions] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState('');
  const [variants, setVariants] = useState([]);
  const [selectedVariantIndexes, setSelectedVariantIndexes] = useState(() => new Set());
  const [showVariantPopup, setShowVariantPopup] = useState(false);
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [groupByOption, setGroupByOption] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [sameHsCodeAcrossVariants, setSameHsCodeAcrossVariants] = useState(false);
  const [variantErrors, setVariantErrors] = useState({}); // Store errors by variant index or SKU
  const [productStatus, setProductStatus] = useState('active');
  
  // Vendor searchable dropdown state
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [vendorLoading, setVendorLoading] = useState(false);
  const vendorDropdownRef = useRef(null);
  const vendorSearchTimeoutRef = useRef(null);

  // Category searchable dropdown state
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const categoryDropdownRef = useRef(null);
  const categorySearchTimeoutRef = useRef(null);

  // Options state
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionValues, setOptionValues] = useState({});
  const [currentOptionValue, setCurrentOptionValue] = useState({});
  const [optionDisplayNames, setOptionDisplayNames] = useState({});
  const [expandedOptionEditors, setExpandedOptionEditors] = useState({});
  const [variantPopupDraft, setVariantPopupDraft] = useState(null);
  const [showVariantImagePicker, setShowVariantImagePicker] = useState(false);
  const [variantImagePickerIndex, setVariantImagePickerIndex] = useState(null);

  /** Last known non-empty value per option slot — keeps variant generation stable while renaming (avoids filtering empty rows). */
  const variantOptionSnapshotsRef = useRef({});
  const formDataRef = useRef(null);
  const sameHsCodeRef = useRef(false);

  /** Stable React keys per option value row (UI-only reordering). */
  const [optionValueRowIds, setOptionValueRowIds] = useState({});

  const makeOptionValueRowId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  const [draggingOptionCardId, setDraggingOptionCardId] = useState(null);
  const [dragOverOptionId, setDragOverOptionId] = useState(null);
  const [dragOverValueDropKey, setDragOverValueDropKey] = useState(null);

  const reorderVariantOptionCards = (sourceOptionId, targetOptionId) => {
    if (!sourceOptionId || !targetOptionId || sourceOptionId === targetOptionId) return;
    setSelectedOptions((prev) => {
      const next = [...prev];
      const fromIdx = next.indexOf(sourceOptionId);
      const toIdx = next.indexOf(targetOptionId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const reorderVariantOptionValues = (optionId, fromIndex, toIndex) => {
    if (
      fromIndex === toIndex ||
      fromIndex === null ||
      toIndex === null ||
      Number.isNaN(fromIndex) ||
      Number.isNaN(toIndex)
    ) {
      return;
    }
    setOptionValues((prevValues) => {
      const arr = [...(prevValues[optionId] || [])];
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= arr.length || toIndex >= arr.length) {
        return prevValues;
      }
      const nextArr = [...arr];
      const [movedVal] = nextArr.splice(fromIndex, 1);
      nextArr.splice(toIndex, 0, movedVal);
      rebuildVariantOptionSnapshotsForOption(optionId, nextArr);

      setOptionValueRowIds((prevIds) => {
        let rowIds = [...(prevIds[optionId] || [])];
        while (rowIds.length < nextArr.length) rowIds.push(makeOptionValueRowId());
        if (rowIds.length > nextArr.length) rowIds = rowIds.slice(0, nextArr.length);
        const nextRowIds = [...rowIds];
        const [movedId] = nextRowIds.splice(fromIndex, 1);
        nextRowIds.splice(toIndex, 0, movedId);
        return { ...prevIds, [optionId]: nextRowIds };
      });

      return { ...prevValues, [optionId]: nextArr };
    });
  };

  /** Keep row id list length aligned with option values (add/remove drift). */
  useEffect(() => {
    setOptionValueRowIds((prev) => {
      const next = { ...prev };
      let changed = false;
      selectedOptions.forEach((optionId) => {
        const valsLen = (optionValues[optionId] || []).length;
        let row = [...(next[optionId] ?? [])];
        let rowChanged = false;
        while (row.length < valsLen) {
          row.push(makeOptionValueRowId());
          rowChanged = true;
        }
        if (row.length > valsLen) {
          row = row.slice(0, valsLen);
          rowChanged = true;
        }
        if (rowChanged) {
          next[optionId] = row;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [optionValues, selectedOptions]);

  // Form state
  const [formData, setFormData] = useState({
    product_id: '',
    title: '',
    sku: '',
    description: '',
    category_id: '',
    price: '',
    compare_price: '',
    inventory_quantity: '',
    image_urls: '',
    weight_kg: '',
    length_cm: '',
    width_cm: '',
    height_cm: '',
    hs_code: '',
    commission_pct: '',
    gst_rate: '',
    tags: []
  });

  // Image upload state
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedUploadedImageIndexes, setSelectedUploadedImageIndexes] = useState([]);
  const [draggedUploadedImageIndex, setDraggedUploadedImageIndex] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');

  const hasVariantOptions = selectedOptions.length > 0;
  const hasVariants = hasVariantOptions;


  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  // Debounced vendor search with server-side API
  const performVendorSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setVendorSuggestions([]);
      setVendorLoading(false);
      return;
    }

    try {
      setVendorLoading(true);
      const response = await searchVendors(searchTerm.trim(), 20);
      
      if (response && Array.isArray(response.results)) {
        // Client-side filtering to ensure only matching results are shown
        const filtered = response.results.filter(vendor => {
          const displayName = (vendor.brand_name || vendor.company_name || '').toLowerCase();
          return displayName.includes(searchTerm.trim().toLowerCase());
        });

        // Sort: items starting with search term come first
        const sorted = filtered.sort((a, b) => {
          const aName = (a.brand_name || a.company_name || '').toLowerCase();
          const bName = (b.brand_name || b.company_name || '').toLowerCase();
          const searchLower = searchTerm.trim().toLowerCase();
          
          const aStarts = aName.startsWith(searchLower);
          const bStarts = bName.startsWith(searchLower);
          
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return aName.localeCompare(bName);
        });

        setVendorSuggestions(sorted);
      } else {
        setVendorSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching vendors:', error);
      setVendorSuggestions([]);
    } finally {
      setVendorLoading(false);
    }
  }, []);
  
  // Handle vendor search change
  const handleVendorSearchChange = (e) => {
    const value = e.target.value;
    setVendorSearchTerm(value);
    setShowVendorDropdown(true);
    
    // Clear vendor_id error when typing
    if (validationErrors.vendor_id) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.vendor_id;
        return newErrors;
      });
    }

    // Debounce the search
    if (vendorSearchTimeoutRef.current) {
      clearTimeout(vendorSearchTimeoutRef.current);
    }

    if (value.trim().length >= 2) {
      setVendorLoading(true);
      vendorSearchTimeoutRef.current = setTimeout(() => {
        performVendorSearch(value);
      }, 300);
    } else {
      setVendorSuggestions([]);
      setVendorLoading(false);
    }
  };
  
  // Handle vendor selection from dropdown
  const handleVendorSelect = (vendor) => {
    const vendorId = vendor ? vendor.id : '';
    const vendorName = vendor ? (vendor.brand_name || vendor.company_name || '') : '';
    console.log('Vendor selected:', { vendorId, vendorName, vendor });
    setSelectedVendorId(vendorId);
    setVendorSearchTerm(vendorName);
    setShowVendorDropdown(false);
    
    // Clear validation error
    if (validationErrors.vendor_id) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.vendor_id;
        return newErrors;
      });
    }
  };
  
  // Highlight matching text in vendor names
  const highlightMatch = (text, searchTerm) => {
    if (!text || !searchTerm || searchTerm.trim().length < 2) {
      return text;
    }
    
    const regex = new RegExp(`(${searchTerm.trim()})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, idx) =>
      regex.test(part) ? <mark key={idx}>{part}</mark> : part
    );
  };

  // Debounced category search with server-side API
  const performCategorySearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setCategorySuggestions([]);
      setCategoryLoading(false);
      return;
    }

    try {
      setCategoryLoading(true);
      const response = await searchCategories(searchTerm.trim(), 20);
      
      if (response && Array.isArray(response.categories)) {
        // Client-side filtering to ensure only matching results are shown
        const filtered = response.categories.filter(category => {
          const displayName = (category.name || '').toLowerCase();
          return displayName.includes(searchTerm.trim().toLowerCase());
        });

        // Sort: items starting with search term come first
        const sorted = filtered.sort((a, b) => {
          const aName = (a.name || '').toLowerCase();
          const bName = (b.name || '').toLowerCase();
          const searchLower = searchTerm.trim().toLowerCase();
          
          const aStarts = aName.startsWith(searchLower);
          const bStarts = bName.startsWith(searchLower);
          
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return aName.localeCompare(bName);
        });

        setCategorySuggestions(sorted);
      } else {
        setCategorySuggestions([]);
      }
    } catch (error) {
      console.error('Error searching categories:', error);
      setCategorySuggestions([]);
    } finally {
      setCategoryLoading(false);
    }
  }, []);
  
  // Handle category search change
  const handleCategorySearchChange = (e) => {
    const value = e.target.value;
    setCategorySearchTerm(value);
    setShowCategoryDropdown(true);
    
    // Clear category_id error when typing
    if (validationErrors.category_id) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.category_id;
        return newErrors;
      });
    }

    // Debounce the search
    if (categorySearchTimeoutRef.current) {
      clearTimeout(categorySearchTimeoutRef.current);
    }

    if (value.trim().length >= 2) {
      setCategoryLoading(true);
      categorySearchTimeoutRef.current = setTimeout(() => {
        performCategorySearch(value);
      }, 300);
    } else {
      setCategorySuggestions([]);
      setCategoryLoading(false);
    }
  };
  
  // Handle category selection from dropdown
  const handleCategorySelect = (category) => {
    if (category) {
      handleInputChange('category_id', category.id);
      setCategorySearchTerm(category.name);
    } else {
      handleInputChange('category_id', '');
      setCategorySearchTerm('');
    }
    setShowCategoryDropdown(false);
  };

  // Handle tag operations
  const handleAddTag = (tag) => {
    if (tag && tag.trim() && !formData.tags.includes(tag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag.trim()]
      }));
    }
  };

  const handleRemoveTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  // Helper function to parse duplicate SKU error and set variant errors
  const handleDuplicateSkuError = (errorMessage) => {
    // Check if error is about duplicate SKU
    if (errorMessage && errorMessage.includes('SKU') && errorMessage.includes('already exists')) {
      // Extract SKU from error message (format: "Variant with SKU 'PROD-BLUE' already exists for this vendor")
      const skuMatch = errorMessage.match(/SKU '([^']+)'/);
      if (skuMatch) {
        const duplicateSku = skuMatch[1];
        
        // Find variant index by SKU
        if (hasVariants && variants.length > 0) {
          const variantIndex = variants.findIndex(v => v.sku === duplicateSku);
          if (variantIndex !== -1) {
            setVariantErrors({
              [variantIndex]: {
                sku: 'This SKU already exists for your vendor. Please use a different SKU.'
              }
            });
            // Scroll to the variant table if it exists
            setTimeout(() => {
              const variantTable = document.querySelector('.tableContainer');
              if (variantTable) {
                variantTable.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }
            }, 100);
            return true;
          }
        } else {
          // Single variant product - set error on formData.sku
          setValidationErrors(prev => ({
            ...prev,
            sku: 'This SKU already exists for your vendor. Please use a different SKU.'
          }));
          return true;
        }
      }
    }
    return false;
  };

  // Handle image file upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Authentication required. Please log in.');
      return;
    }

    setUploadingImages(true);
    setImageUploadError('');

    const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 25MB as per backend

    try {
      const uploadPromises = files.map(async (file) => {
        // Validate file type
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
          throw new Error(`${file.name}: Unsupported file type. Please upload JPG, PNG, GIF, or WebP images.`);
        }

        // Validate file size
        if (file.size > MAX_IMAGE_SIZE) {
          throw new Error(`${file.name}: File size exceeds 25MB limit.`);
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('file_type', 'image');

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/core/s3/upload/`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (!res.ok) {
          const err = await res.json().catch(() => null);
          throw new Error(err?.error || `Failed to upload ${file.name}`);
        }

        return res.json();
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      
      setUploadedImages(prev => [
        ...prev,
        ...uploadedFiles.map(upload => ({
          url: upload.file_url,
          fileName: upload.filename,
          fileId: upload.file_id
        }))
      ]);

      // Clear validation error for images
      if (validationErrors['images']) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors['images'];
          return newErrors;
        });
      }

    } catch (error) {
      console.error('Image upload failed:', error);
      setImageUploadError(error.message || 'Failed to upload images. Please try again.');
    } finally {
      setUploadingImages(false);
      // Clear the input so the same file can be selected again
      e.target.value = '';
    }
  };

  // Remove uploaded image
  const handleRemoveImage = (index) => {
    const removedUrl = uploadedImages[index]?.url;
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setSelectedUploadedImageIndexes((prev) =>
      prev
        .filter((selectedIndex) => selectedIndex !== index)
        .map((selectedIndex) => (selectedIndex > index ? selectedIndex - 1 : selectedIndex))
    );
    // A removed image can no longer be assigned to a variant — clear any
    // variant that was pointing at it so it doesn't submit a dangling URL.
    if (removedUrl) {
      clearVariantImageReferences(removedUrl);
    }
  };

  const toggleUploadedImageSelection = (index) => {
    setSelectedUploadedImageIndexes((prev) =>
      prev.includes(index) ? prev.filter((value) => value !== index) : [...prev, index]
    );
  };

  const handleBulkRemoveUploadedImages = () => {
    if (selectedUploadedImageIndexes.length === 0) return;
    const selectedSet = new Set(selectedUploadedImageIndexes);
    const removedUrls = uploadedImages
      .filter((_, index) => selectedSet.has(index))
      .map((img) => img.url)
      .filter(Boolean);
    setUploadedImages((prev) => prev.filter((_, index) => !selectedSet.has(index)));
    setSelectedUploadedImageIndexes([]);
    removedUrls.forEach((url) => clearVariantImageReferences(url));
  };

  const reorderUploadedImages = (sourceIndex, targetIndex) => {
    if (sourceIndex === targetIndex || sourceIndex === null || targetIndex === null) return;

    setUploadedImages((prev) => {
      if (
        sourceIndex < 0 ||
        targetIndex < 0 ||
        sourceIndex >= prev.length ||
        targetIndex >= prev.length
      ) {
        return prev;
      }
      const next = [...prev];
      const [movedItem] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, movedItem);
      return next;
    });

    setSelectedUploadedImageIndexes((prev) => {
      if (!prev.length) return prev;
      return prev.map((selectedIndex) => {
        if (selectedIndex === sourceIndex) return targetIndex;
        if (sourceIndex < targetIndex && selectedIndex > sourceIndex && selectedIndex <= targetIndex) {
          return selectedIndex - 1;
        }
        if (sourceIndex > targetIndex && selectedIndex >= targetIndex && selectedIndex < sourceIndex) {
          return selectedIndex + 1;
        }
        return selectedIndex;
      });
    });
  };

  // Real-time validation
  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'title':
        if (!value || value.trim() === '') {
          error = 'Title is required';
        } else if (value.length > 500) {
          error = 'Title must not exceed 500 characters';
        }
        break;

      case 'description':
        // Description validation removed entirely
        break;

      case 'sku':
        if (value && value.length > 100) {
          error = 'SKU must be 100 characters or less';
        }
        if (value && (value.startsWith(' ') || value.endsWith(' '))) {
          error = 'SKU must not have leading or trailing spaces';
        }
        break;

      case 'category_id':
        if (!value || value.trim() === '') {
          error = 'Category is required';
        }
        break;

      case 'price':
        if (value !== '' && (isNaN(value) || parseFloat(value) < 0)) {
          error = 'Price must be 0 or greater';
        }
        break;

      case 'compare_price':
        if (value && value !== '' && (isNaN(value) || parseFloat(value) < 0)) {
          error = 'Compare price must be 0 or greater';
        }
        break;

      case 'gst_rate':
        if (value && value !== '' && (isNaN(value) || parseFloat(value) < 0 || parseFloat(value) > 100)) {
          error = 'GST rate must be between 0 and 100';
        }
        break;

      case 'hs_code':
        if (value && value !== '' && (!/^\d{4,8}$/.test(value))) {
          error = 'HS code must be 6–13 digits';
        }
        break;

      case 'weight_kg':
        if (value && value !== '' && (isNaN(value) || parseFloat(value) <= 0)) {
          error = 'Weight must be greater than 0';
        }
        break;

      case 'length_cm':
      case 'width_cm':
      case 'height_cm':
        if (value && (isNaN(value) || parseFloat(value) < 0)) {
          error = 'Dimension must be a non-negative number';
        }
        break;

      case 'commission_pct':
        if (value && (isNaN(value) || parseFloat(value) < 0 || parseFloat(value) > 100)) {
          error = 'Commission must be between 0 and 100';
        }
        break;

      case 'inventory_quantity':
        if (value === '' || value === null) {
          error = 'Inventory quantity is required';
        } else if (!Number.isInteger(Number(value)) || Number(value) < 0) {
          error = 'Inventory must be a non-negative integer';
        }
        break;

      case 'image_urls':
        // Remove validation for image_urls since we're using file upload now
        break;
      default:
        break;
    }

    return error;
  };

  // Validate variant field
  const validateVariantField = (variantIndex, field, value) => {
    let error = '';

    switch (field) {
      case 'sku':
        if (value && value.length > 100) {
          error = 'SKU must be 100 characters or less';
        }
        if (value && (value.startsWith(' ') || value.endsWith(' '))) {
          error = 'SKU must not have leading or trailing spaces';
        }
        break;

      case 'price':
        if (value !== '' && (isNaN(value) || parseFloat(value) < 0)) {
          error = 'Price must be 0 or greater';
        }
        break;

      case 'compare_price':
        if (value && value !== '' && (isNaN(value) || parseFloat(value) < 0)) {
          error = 'Compare price must be 0 or greater';
        }
        break;

      case 'gst_rate':
        if (value && value !== '' && (isNaN(value) || parseFloat(value) < 0 || parseFloat(value) > 100)) {
          error = 'GST rate must be between 0 and 100';
        }
        break;

      case 'hs_code':
        if (value && value !== '' && (!/^\d{4,8}$/.test(value))) {
          error = 'HS code must be 6–13 digits';
        }
        break;

      case 'weight_kg':
        if (value && value !== '' && (isNaN(value) || parseFloat(value) <= 0)) {
          error = 'Weight must be greater than 0';
        }
        break;

      default:
        break;
    }

    return error;
  };

  // Validate all required fields before submission
  const validateAllFields = () => {
    const errors = {};
    const variantValidationErrors = {};

    // Always validate title (required)
    const titleError = validateField('title', formData.title);
    if (titleError) {
      errors.title = titleError;
    }

    // Always validate category (required)
    const categoryError = validateField('category_id', formData.category_id);
    if (categoryError) {
      errors.category_id = categoryError;
    }

    // Only validate single variant fields if hasVariants is false
    if (!hasVariants) {
      const singleVariantFields = ['sku', 'price', 'inventory_quantity', 'compare_price', 'gst_rate', 'hs_code', 'weight_kg'];

      singleVariantFields.forEach(field => {
        const error = validateField(field, formData[field]);
        if (error) {
          errors[field] = error;
        }
      });

      // Validate images
      if (uploadedImages.length === 0) {
        errors['images'] = 'At least one image is required';
      }
    } else {
      // Validate each variant
      variants.forEach((variant, index) => {
        const variantErrors = {};
        
        ['sku', 'price', 'compare_price', 'gst_rate', 'hs_code', 'weight_kg'].forEach(field => {
          const error = validateVariantField(index, field, variant[field]);
          if (error) {
            variantErrors[field] = error;
          }
        });

        if (Object.keys(variantErrors).length > 0) {
          variantValidationErrors[index] = variantErrors;
        }
      });
    }

    // Validate vendor_id for admin users
    if (userRole === 'admin' && !selectedVendorId) {
      errors.vendor_id = 'Vendor is required for admin users';
    }

    setValidationErrors(errors);
    setVariantErrors(variantValidationErrors);
    
    const totalErrors = Object.keys(errors).length + Object.keys(variantValidationErrors).length;
    
    if (totalErrors > 0) {
      setSaveStatus({
        type: 'error',
        message: `Please fix ${totalErrors} error${totalErrors > 1 ? 's' : ''} before saving.`
      });
    }
    
    return totalErrors === 0;
  };

  // Parse backend 400 error details and map to specific fields
  const handleBackendValidationErrors = (error) => {
    if (error?.status === 400 && error?.details && Array.isArray(error.details)) {
      const errors = {};
      const variantValidationErrors = {};

      error.details.forEach(detail => {
        // Parse variants[0].field_name pattern
        const variantMatch = detail.match(/variants\[(\d+)\]\.(.+)/);
        if (variantMatch) {
          const variantIndex = parseInt(variantMatch[1]);
          const fieldName = variantMatch[2];
          
          if (!variantValidationErrors[variantIndex]) {
            variantValidationErrors[variantIndex] = {};
          }
          
          // Map backend field names to our validation messages
          let errorMessage = detail;
          if (fieldName === 'hs_code') {
            errorMessage = 'HSN code must be 4 to 8 digits (numbers only)';
          } else if (fieldName === 'price') {
            errorMessage = 'Price must be 0 or greater';
          } else if (fieldName === 'compare_price') {
            errorMessage = 'Compare price must be 0 or greater';
          } else if (fieldName === 'gst_rate') {
            errorMessage = 'GST rate must be between 0 and 100';
          } else if (fieldName === 'weight_kg') {
            errorMessage = 'Weight must be greater than 0';
          } else if (fieldName === 'sku') {
            if (detail.includes('required')) {
              errorMessage = 'SKU is required';
            } else if (detail.includes('characters')) {
              errorMessage = 'SKU must be 100 characters or less';
            } else if (detail.includes('spaces')) {
              errorMessage = 'SKU must not have leading or trailing spaces';
            }
          }
          
          variantValidationErrors[variantIndex][fieldName] = errorMessage;
        } else {
          // Handle product-level errors
          let fieldName = detail.split(' ')[0].toLowerCase();
          if (fieldName === 'title') {
            errors.title = detail;
          } else if (fieldName === 'description') {
            errors.description = detail;
          } else if (fieldName.includes('category')) {
            errors.category_id = detail;
          }
        }
      });

      setValidationErrors(errors);
      setVariantErrors(variantValidationErrors);
      
      const totalErrors = Object.keys(errors).length + Object.keys(variantValidationErrors).length;
      setSaveStatus({
        type: 'error',
        message: `Please fix ${totalErrors} error${totalErrors > 1 ? 's' : ''} before saving.`
      });
      
      return true;
    }
    return false;
  };

  // Load categories and options
  useEffect(() => {
    let cancelled = false;
    const fetchCats = async () => {
      try {
        setLoadingCategories(true);
        setCategoriesError('');
        const resp = await listCategories();
        const list = Array.isArray(resp?.categories) ? resp.categories : [];
        const options = Array.isArray(resp?.variants_options) ? resp.variants_options : [];
        if (!cancelled) {
          setCategories(list);
          setVariantsOptions(options);
        }
      } catch (e) {
        if (!cancelled) setCategoriesError(typeof e?.message === 'string' ? e.message : 'Failed to load categories');
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    };
    fetchCats();
    return () => { cancelled = true; };
  }, []);

  // Close vendor dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initialize category search term from selected category
  useEffect(() => {
    if (formData.category_id && categories.length > 0) {
      const category = categories.find(c => c.id === formData.category_id);
      if (category) {
        setCategorySearchTerm(category.name);
      }
    }
  }, [formData.category_id, categories]);

  const normalizeNumberValue = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const parsed = Number(value);
    return Number.isNaN(parsed) ? '' : parsed;
  };

  const variantOptionSnapshotKey = (optionId, valueIndex) => `${optionId}-${valueIndex}`;

  /** Value used for variant names / cartesian product while a slot may be temporarily empty in the UI. */
  const resolveOptionValueForVariants = (optionId, valueIndex, raw) => {
    const trimmed = String(raw ?? '').trim();
    if (trimmed) return trimmed;
    const snap = variantOptionSnapshotsRef.current[variantOptionSnapshotKey(optionId, valueIndex)];
    if (snap && String(snap).trim()) return String(snap).trim().charAt(0);
    return '';
  };

  const rebuildVariantOptionSnapshotsForOption = (optionId, rawValues) => {
    Object.keys(variantOptionSnapshotsRef.current).forEach((key) => {
      if (key.startsWith(`${optionId}-`)) delete variantOptionSnapshotsRef.current[key];
    });
    (rawValues || []).forEach((v, idx) => {
      const t = String(v ?? '').trim();
      if (t) {
        variantOptionSnapshotsRef.current[variantOptionSnapshotKey(optionId, idx)] = t;
      }
    });
  };

  formDataRef.current = formData;
  sameHsCodeRef.current = sameHsCodeAcrossVariants;

  // Generate variants from options (Cartesian product)
  const generateVariantsFromOptions = useCallback(() => {
    if (
      selectedOptions.length === 0 ||
      selectedOptions.some((id) => {
        const committed = optionValues[id] || [];
        const typing = (currentOptionValue[id] || '').trim();
        return committed.length === 0 && !typing;
      })
    ) {
      setVariants([]);
      return;
    }

    // Build arrays of values for each option
    const optionArrays = selectedOptions.map(optionId => {
      const option = variantsOptions.find(o => o.id === optionId);
      const optionName = optionDisplayNames[optionId] || option?.name || '';
      const rawValues = optionValues[optionId] || [];
      const typingValue = (currentOptionValue[optionId] || '').trim();
      const valuesWithTyping = typingValue && !rawValues.map((v) => String(v).trim()).includes(typingValue)
        ? [...rawValues, typingValue]
        : rawValues;
      return valuesWithTyping
        .map((raw, valueIndex) => ({
          optionId,
          optionName,
          valueIndex,
          value: resolveOptionValueForVariants(optionId, valueIndex, raw),
        }))
        .filter((item) => item.value !== '');
    });

    // Calculate Cartesian product
    const cartesianProduct = (arrays) => {
      if (arrays.length === 0) return [[]];
      const [first, ...rest] = arrays;
      const restProduct = cartesianProduct(rest);
      return first.flatMap(item => restProduct.map(items => [item, ...items]));
    };

    if (optionArrays.some((items) => items.length === 0)) {
      setVariants([]);
      return;
    }

    const combinations = cartesianProduct(optionArrays);

    // Identity key for matching a regenerated combination back to its previous
    // variant row: (optionId, valueIndex) pairs are stable across a value's
    // TEXT being edited (e.g. fixing a typo), unlike the rendered name string —
    // so an in-progress field like an assigned image_urls isn't silently
    // discarded just because the vendor renamed an option value.
    const comboPositionKey = (combo) =>
      combo.map((c) => `${c.optionId}:${c.valueIndex}`).join('|');

    setVariants((prevVariants) => {
      const existingVariantByPosition = new Map(
        prevVariants.map((variant) => [variant._comboKey, variant])
      );
      const existingVariantByName = new Map(
        prevVariants.map((variant) => [variant.name, variant])
      );

      return combinations.map((combo) => {
        const name = combo.map((c) => c.value).join(' | ');
        const comboKey = comboPositionKey(combo);
        const optionValuesList = buildOptionValuesFromCombo(combo);
        // Prefer matching by stable position; fall back to name-matching for
        // rows carried over before this key existed (e.g. mid-session).
        const existing = existingVariantByPosition.get(comboKey) || existingVariantByName.get(name);

        const fd = formDataRef.current;
        const sameHsCode = sameHsCodeRef.current;
        return {
          sku: existing?.sku || '',
          name,
          _comboKey: comboKey,
          option_values: optionValuesList,
          image_urls: existing?.image_urls || '',
          inventory_quantity: existing?.inventory_quantity || '',
          price: existing?.price || fd.price || '',
          compare_price: existing?.compare_price || fd.compare_price || '',
          weight_kg: existing?.weight_kg || fd.weight_kg || '',
          length_cm: existing?.length_cm || fd.length_cm || '',
          width_cm: existing?.width_cm || fd.width_cm || '',
          height_cm: existing?.height_cm || fd.height_cm || '',
          hs_code: sameHsCode ? (fd.hs_code || '') : '',
          commission_pct: existing?.commission_pct || fd.commission_pct || '',
          gst_rate: existing?.gst_rate || fd.gst_rate || ''
        };
      });
    });
    // Clear variant errors when variants are regenerated
    setVariantErrors({});
  }, [selectedOptions, optionValues, currentOptionValue, variantsOptions]);

  useEffect(() => {
    if (hasVariantOptions) {
      generateVariantsFromOptions();
    } else {
      setVariants([]);
    }
  }, [hasVariantOptions, currentOptionValue, generateVariantsFromOptions]);

  useEffect(() => {
    setSelectedVariantIndexes((prev) => {
      const next = new Set([...prev].filter((i) => i >= 0 && i < variants.length));
      if (next.size === prev.size) {
        for (const i of prev) {
          if (!next.has(i)) return next;
        }
        return prev;
      }
      return next;
    });
  }, [variants]);

  const toggleVariantSelection = useCallback((originalIndex) => {
    setSelectedVariantIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(originalIndex)) next.delete(originalIndex);
      else next.add(originalIndex);
      return next;
    });
  }, []);

  const handleVariantsHeaderSelectionClick = useCallback((e) => {
    e.stopPropagation();
    setSelectedVariantIndexes((prev) => {
      const total = variants.length;
      if (total === 0) return prev;
      let selectedCount = 0;
      for (let i = 0; i < total; i++) {
        if (prev.has(i)) selectedCount++;
      }
      if (selectedCount === total) return new Set();
      return new Set(Array.from({ length: total }, (_, idx) => idx));
    });
  }, [variants.length]);

  // Handle adding an option
  const handleAddOption = (optionId) => {
    if (selectedOptions.length >= 3) {
      alert('Maximum 3 options allowed');
      return;
    }
    if (!selectedOptions.includes(optionId)) {
      setSelectedOptions(prev => [...prev, optionId]);
      setOptionValues(prev => ({ ...prev, [optionId]: [] }));
      setOptionValueRowIds((prev) => ({ ...prev, [optionId]: [] }));
      setCurrentOptionValue(prev => ({ ...prev, [optionId]: '' }));
      const option = variantsOptions.find((opt) => opt.id === optionId);
      setOptionDisplayNames((prev) => ({ ...prev, [optionId]: option?.name || '' }));
      setExpandedOptionEditors((prev) => ({ ...prev, [optionId]: true }));
      if (!groupByOption) {
        if (option?.name) {
          setGroupByOption(option.name);
        }
      }
    }
  };

  // Handle adding an option just created (or reused, if it already existed
  // under a different case) via the OptionNameCombobox's "Create" affordance.
  // Unlike the old custom-option flow, this ID is a real, persisted
  // TerminalVariantOptionCatalog row — never a client-side temp ID.
  const handleAddCreatedOption = (createdOpt) => {
    setVariantsOptions((prev) =>
      prev.some((o) => o.id === createdOpt.id) ? prev : [...prev, createdOpt]
    );
    handleAddOption(createdOpt.id); // guards the max-3-options limit itself
  };

  // Handle removing an option
  const handleRemoveOption = (optionId) => {
    setSelectedOptions(prev => prev.filter(id => id !== optionId));
    setOptionValues(prev => {
      const newValues = { ...prev };
      delete newValues[optionId];
      return newValues;
    });
    setCurrentOptionValue(prev => {
      const newValues = { ...prev };
      delete newValues[optionId];
      return newValues;
    });
    setOptionDisplayNames((prev) => {
      const next = { ...prev };
      delete next[optionId];
      return next;
    });
    setExpandedOptionEditors((prev) => {
      const next = { ...prev };
      delete next[optionId];
      return next;
    });
    setOptionValueRowIds((prev) => {
      const next = { ...prev };
      delete next[optionId];
      return next;
    });
    const removedOptionName = variantsOptions.find((option) => option.id === optionId)?.name;
    if (removedOptionName && groupByOption === removedOptionName) {
      const nextOptionId = selectedOptions.find((id) => id !== optionId);
      const nextOptionName = variantsOptions.find((option) => option.id === nextOptionId)?.name || '';
      setGroupByOption(nextOptionName);
    }
  };

  // Handle adding a value to an option
  const handleAddOptionValue = (optionId, value) => {
    if (value && value.trim()) {
      const trimmed = value.trim();
      setOptionValues((prev) => {
        const list = [...(prev[optionId] || [])];
        if (!list.map((x) => String(x).trim()).includes(trimmed)) {
          list.push(trimmed);
        }
        rebuildVariantOptionSnapshotsForOption(optionId, list);
        return { ...prev, [optionId]: list };
      });
      setCurrentOptionValue((prev) => ({ ...prev, [optionId]: '' }));
    }
  };

  const handleOptionValueInputChange = (optionId, valueIndex, value) => {
    const trimmed = String(value ?? '').trim();
    if (trimmed) {
      variantOptionSnapshotsRef.current[variantOptionSnapshotKey(optionId, valueIndex)] = trimmed;
    }
    setOptionValues((prev) => {
      const currentValues = [...(prev[optionId] || [])];
      currentValues[valueIndex] = value;
      return {
        ...prev,
        [optionId]: currentValues
      };
    });
  };

  const handleCommittedOptionValueBlur = (optionId, valueIndex, raw) => {
    const trimmed = String(raw ?? '').trim();
    const key = variantOptionSnapshotKey(optionId, valueIndex);
    const snap = variantOptionSnapshotsRef.current[key];
    if (trimmed) return;
    if (snap && String(snap).trim()) {
      handleOptionValueInputChange(optionId, valueIndex, String(snap).trim().charAt(0));
    }
  };

  // Handle removing a value from an option
  const handleRemoveOptionValue = (optionId, valueIndex) => {
    setOptionValues((prev) => {
      const next = [...(prev[optionId] || [])].filter((_, i) => i !== valueIndex);
      rebuildVariantOptionSnapshotsForOption(optionId, next);
      return { ...prev, [optionId]: next };
    });
    setOptionValueRowIds((prev) => {
      const ids = [...(prev[optionId] || [])];
      ids.splice(valueIndex, 1);
      return { ...prev, [optionId]: ids };
    });
  };

  // Handle key press in option value input
  const handleOptionValueKeyPress = (e, optionId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOptionValue(optionId, currentOptionValue[optionId]);
    }
  };

  const handleInlineVariantChange = (index, field, value) => {
    setVariants((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });

    // Validate the field and clear error if valid
    const error = validateVariantField(index, field, value);
    setVariantErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        if (!newErrors[index]) {
          newErrors[index] = {};
        }
        newErrors[index][field] = error;
      } else {
        if (newErrors[index]) {
          delete newErrors[index][field];
          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
      }
      return newErrors;
    });
  };

  const handleInlineVariantImageChange = (index, imageUrl) => {
    handleInlineVariantChange(index, 'image_urls', imageUrl ? imageUrl : '');
  };

  // Strip a removed image's URL out of every variant's image_urls so a
  // variant never submits a reference to an image that's no longer uploaded.
  const clearVariantImageReferences = (removedUrl) => {
    setVariants((prev) =>
      prev.map((variant) => {
        if (!variant.image_urls) return variant;
        const remaining = variant.image_urls
          .split('|')
          .map((url) => url.trim())
          .filter((url) => url && url !== removedUrl);
        if (remaining.length === variant.image_urls.split('|').filter(Boolean).length) {
          return variant;
        }
        return { ...variant, image_urls: remaining.join('|') };
      })
    );
  };

  const openVariantDetailsModal = (index) => {
    handleEditVariant(index);
  };

  const openVariantImagePicker = (index) => {
    setVariantImagePickerIndex(index);
    setShowVariantImagePicker(true);
  };

  const handleSelectVariantImage = (url) => {
    if (variantImagePickerIndex === null) return;
    handleInlineVariantImageChange(variantImagePickerIndex, url);
    setShowVariantImagePicker(false);
    setVariantImagePickerIndex(null);
  };

  // Handle Save Draft
  const handleSaveDraft = async () => {
    if (isSaving) return;
    // Validate that no blob URLs are being saved - these are local-only URLs that won't work on Shopify
    if (hasBlobUrls(uploadedImages)) {
      setSaveStatus({
        type: 'error',
        message: 'Some images failed to upload properly. Please remove the failed images and re-upload them.'
      });
      setImageUploadError('Some images have invalid URLs (blob:). Please remove and re-upload them.');
      return;
    }

    setIsSaving(true);
    setSaveStatus(null);

    try {
      let variantsArray = [];
      let allMedia = [];

      // Product-level images: every uploaded image gets one media entry. The
      // variant→image link is carried on the variant payload (image_url), not
      // by repeating the URL inside `media` — keeps the backend writes simple
      // and avoids URL-dedup gymnastics.
      allMedia = uploadedImages.map((img, index) => ({
        url: img.url,
        is_primary: index === 0,
        position: index,
      }));

      if (hasVariantOptions && variants.length > 0) {
        variantsArray = variants.map((variant) => {
          const firstImageUrl = (variant.image_urls || '')
            .split('|')
            .map(s => s.trim())
            .filter(Boolean)[0] || null;

          return {
            sku: variant.sku,
            price: variant.price || formData.price || 0,
            compare_price: variant.compare_price || undefined,
            weight_kg: variant.weight_kg || formData.weight_kg || undefined,
            length_cm: variant.length_cm || formData.length_cm || undefined,
            width_cm: variant.width_cm || formData.width_cm || undefined,
            height_cm: variant.height_cm || formData.height_cm || undefined,
            // Use formData.hs_code if toggle is on, otherwise use variant-specific hs_code
            hs_code: sameHsCodeAcrossVariants ? (formData.hs_code || undefined) : (variant.hs_code || undefined),
            commission_pct: variant.commission_pct || undefined,
            gst_rate: variant.gst_rate ?? formData.gst_rate ?? undefined,
            option_values: variant.option_values || {},
            name: variant.name || '',
            // Reference to a product-level image — backend will set ProductMedia.variant
            // on the matching row.
            image_url: firstImageUrl,
            meta: {
              inventory: variant.inventory_quantity ? Number(variant.inventory_quantity) : (formData.inventory_quantity ? Number(formData.inventory_quantity) : 0),
            },
          };
        });
      } else {
        const variant = {
          sku: formData.sku,
          price: formData.price,
          compare_price: formData.compare_price || undefined,
          weight_kg: formData.weight_kg || undefined,
          length_cm: formData.length_cm || undefined,
          width_cm: formData.width_cm || undefined,
          height_cm: formData.height_cm || undefined,
          hs_code: formData.hs_code || undefined,
          commission_pct: formData.commission_pct || undefined,
          gst_rate: formData.gst_rate || undefined,
          meta: {
            inventory: formData.inventory_quantity ? Number(formData.inventory_quantity) : 0,
          },
        };

        variantsArray = [variant];
      }

      const payload = {
        title: formData.title,
        description: formData.description || '',
        category_id: formData.category_id || null,
        // The vendor/admin-chosen Shopify status (active/draft) — must be
        // preserved through approval, never forced to active on push.
        terminal_status: productStatus,
        has_variants: hasVariantOptions && variants.length > 0,
        meta: {
          tags: formData.tags || [],
        },
        variants: variantsArray,
        media: allMedia,
      };

      if (userRole === 'admin' && selectedVendorId) {
        payload.vendor_id = selectedVendorId;
        console.log('Admin user - Setting vendor_id in payload:', selectedVendorId);
      } else {
        console.log('Not setting vendor_id - userRole:', userRole, 'selectedVendorId:', selectedVendorId);
      }

      console.log('Final payload being sent to createProduct:', JSON.stringify(payload, null, 2));
      const resp = await createProduct(payload);
      if (!resp?.product) throw new Error('Create failed');

      setSaveStatus({
        type: 'success',
        message: 'Product saved as draft successfully!'
      });

      setTimeout(() => leaveFormFor('manage'), 2000);

    } catch (error) {
      console.error('Error saving draft:', error);
      
      // Clear previous variant errors
      setVariantErrors({});
      
      // Try to handle backend validation errors first
      if (handleBackendValidationErrors(error)) {
        return; // Error already handled by the validation error handler
      }
      
      const errorMessage = error?.message || 'Failed to save product as draft. Please try again.';
      
      // Check if it's a duplicate SKU error
      if (handleDuplicateSkuError(errorMessage)) {
        setSaveStatus({
          type: 'error',
          message: errorMessage
        });
      } else {
        setSaveStatus({
          type: 'error',
          message: errorMessage
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Submit for Approval
  const handleSubmitForApproval = async () => {
    if (isSaving) return;
    if (!validateAllFields()) {
      setSaveStatus({
        type: 'error',
        message: 'Please fix all validation errors before submitting for approval.'
      });
      return;
    }

    // Validate that no blob URLs are being saved - these are local-only URLs that won't work on Shopify
    if (hasBlobUrls(uploadedImages)) {
      setSaveStatus({
        type: 'error',
        message: 'Some images failed to upload properly. Please remove the failed images and re-upload them.'
      });
      setImageUploadError('Some images have invalid URLs (blob:). Please remove and re-upload them.');
      return;
    }

    if (hasVariants) {
      if (variants.length === 0) {
        setSaveStatus({
          type: 'error',
          message: 'Please add at least one variant before submitting.'
        });
        return;
      }

      for (let i = 0; i < variants.length; i++) {
        const variant = variants[i];
        if (!variant.sku || variant.sku.trim() === '') {
          setSaveStatus({
            type: 'error',
            message: `Variant ${i + 1} is missing a SKU.`
          });
          return;
        }
        if (!variant.price || variant.price === '') {
          setSaveStatus({
            type: 'error',
            message: `Variant ${i + 1} is missing a price.`
          });
          return;
        }
      }

      // A variant image is optional — images can be stored and pushed at the
      // product level without a per-variant assignment. Still require at
      // least one product-level image, mirroring the no-variants validation above.
      if (uploadedImages.length === 0) {
        setSaveStatus({
          type: 'error',
          message: 'At least one image is required.'
        });
        return;
      }
    }

    setIsSaving(true);
    setSaveStatus(null);

    try {
      let variantsArray = [];
      let allMedia = [];

      // Product-level images: every uploaded image gets one media entry. The
      // variant→image link is carried on the variant payload (image_url), not
      // by repeating the URL inside `media` — keeps the backend writes simple
      // and avoids URL-dedup gymnastics.
      allMedia = uploadedImages.map((img, index) => ({
        url: img.url,
        is_primary: index === 0,
        position: index,
      }));

      if (hasVariantOptions && variants.length > 0) {
        variantsArray = variants.map((variant) => {
          const firstImageUrl = (variant.image_urls || '')
            .split('|')
            .map(s => s.trim())
            .filter(Boolean)[0] || null;

          return {
            sku: variant.sku,
            price: variant.price || formData.price || 0,
            compare_price: variant.compare_price || undefined,
            weight_kg: variant.weight_kg || formData.weight_kg || undefined,
            length_cm: variant.length_cm || formData.length_cm || undefined,
            width_cm: variant.width_cm || formData.width_cm || undefined,
            height_cm: variant.height_cm || formData.height_cm || undefined,
            // Use formData.hs_code if toggle is on, otherwise use variant-specific hs_code
            hs_code: sameHsCodeAcrossVariants ? (formData.hs_code || undefined) : (variant.hs_code || undefined),
            commission_pct: variant.commission_pct || undefined,
            gst_rate: variant.gst_rate ?? formData.gst_rate ?? undefined,
            option_values: variant.option_values || {},
            name: variant.name || '',
            // Reference to a product-level image — backend will set ProductMedia.variant
            // on the matching row.
            image_url: firstImageUrl,
            meta: {
              inventory: variant.inventory_quantity ? Number(variant.inventory_quantity) : (formData.inventory_quantity ? Number(formData.inventory_quantity) : 0),
            },
          };
        });
      } else {
        const variant = {
          sku: formData.sku,
          price: formData.price,
          compare_price: formData.compare_price || undefined,
          weight_kg: formData.weight_kg || undefined,
          length_cm: formData.length_cm || undefined,
          width_cm: formData.width_cm || undefined,
          height_cm: formData.height_cm || undefined,
          hs_code: formData.hs_code || undefined,
          commission_pct: formData.commission_pct || undefined,
          gst_rate: formData.gst_rate || undefined,
          meta: {
            inventory: formData.inventory_quantity ? Number(formData.inventory_quantity) : 0,
          },
        };

        variantsArray = [variant];
      }

      const payload = {
        title: formData.title,
        description: formData.description || '',
        category_id: formData.category_id || null,
        // The vendor/admin-chosen Shopify status (active/draft) — must be
        // preserved through approval, never forced to active on push. The product
        // always goes to pending approval on create server-side; no separate
        // 'pending_approval' marker is needed (or valid) here.
        terminal_status: productStatus,
        has_variants: hasVariantOptions && variants.length > 0,
        meta: {
          tags: formData.tags || [],
        },
        variants: variantsArray,
        media: allMedia,
      };

      if (userRole === 'admin' && selectedVendorId) {
        payload.vendor_id = selectedVendorId;
        console.log('Admin user - Setting vendor_id in payload:', selectedVendorId);
      } else {
        console.log('Not setting vendor_id - userRole:', userRole, 'selectedVendorId:', selectedVendorId);
      }

      console.log('Final payload being sent to createProduct:', JSON.stringify(payload, null, 2));
      const resp = await createProduct(payload);
      if (!resp?.product) throw new Error('Create failed');

      setSaveStatus({
        type: 'success',
        message: 'Product submitted for approval successfully!'
      });

      setTimeout(() => leaveFormFor('pending'), 2000);

    } catch (error) {
      console.error('Error submitting for approval:', error);
      
      // Clear previous variant errors
      setVariantErrors({});
      
      // Try to handle backend validation errors first
      if (handleBackendValidationErrors(error)) {
        return; // Error already handled by the validation error handler
      }
      
      const errorMessage = error?.message || 'Failed to submit product for approval. Please try again.';
      
      // Check if it's a duplicate SKU error
      if (handleDuplicateSkuError(errorMessage)) {
        setSaveStatus({
          type: 'error',
          message: errorMessage
        });
      } else {
        setSaveStatus({
          type: 'error',
          message: errorMessage
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleBlur = (field) => {
    const error = validateField(field, formData[field]);
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: error
      }));
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      closeForm();
    }
  };

  // Handle same HS code toggle change
  const handleSameHsCodeToggle = (checked) => {
    setSameHsCodeAcrossVariants(checked);
    if (checked && variants.length > 0) {
      // When enabling, apply formData.hs_code to all variants
      setVariants(prev => prev.map(v => ({ ...v, hs_code: formData.hs_code || '' })));
    }
  };

  const handleDeleteVariant = (index) => {
    if (window.confirm('Are you sure you want to delete this variant?')) {
      setVariants(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleEditVariant = (index) => {
    const variant = variants[index];
    setEditingVariantIndex(index);
    setVariantPopupDraft({
      ...variant,
      inventory_quantity: normalizeNumberValue(variant.inventory_quantity),
      price: normalizeNumberValue(variant.price),
      compare_price: normalizeNumberValue(variant.compare_price),
      weight_kg: normalizeNumberValue(variant.weight_kg) || normalizeNumberValue(formData.weight_kg),
      length_cm: normalizeNumberValue(variant.length_cm) || normalizeNumberValue(formData.length_cm),
      width_cm: normalizeNumberValue(variant.width_cm) || normalizeNumberValue(formData.width_cm),
      height_cm: normalizeNumberValue(variant.height_cm) || normalizeNumberValue(formData.height_cm),
      hs_code: sameHsCodeAcrossVariants ? (formData.hs_code || '') : (variant.hs_code || ''),
      gst_rate: normalizeNumberValue(variant.gst_rate) || normalizeNumberValue(formData.gst_rate),
    });
    setShowVariantPopup(true);
  };

  const handleVariantSave = () => {
    if (editingVariantIndex !== null) {
      setVariants(prev => {
        const updated = [...prev];
        updated[editingVariantIndex] = {
          ...updated[editingVariantIndex],
          ...variantPopupDraft,
          // Use formData.hs_code if toggle is on, otherwise use the value from formDataFromView
          hs_code: sameHsCodeAcrossVariants ? (formData.hs_code || '') : (variantPopupDraft.hs_code || ''),
        };
        return updated;
      });

      // Clear variant error when saving
      if (variantErrors[editingVariantIndex]) {
        setVariantErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[editingVariantIndex];
          return newErrors;
        });
      }

      setShowVariantPopup(false);
      setVariantPopupDraft(null);
      setEditingVariantIndex(null);
    }
  };

  const handleCloseVariantEditor = () => {
    setShowVariantPopup(false);
    setVariantPopupDraft(null);
    setEditingVariantIndex(null);
  };

  const renderBasicInfo = () => (
    <div className="formSection">
      <div className="formGrid">
        <div className="formField fullWidth">
          <label className="formLabel">
            Title <span className="required">*</span>
            <span className={`characterCounter ${formData.title.length > 500 ? 'error' : ''}`}>
              {formData.title.length}/500
            </span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            className={`formInput ${validationErrors.title ? 'error' : ''} ${formData.title.length > 500 ? 'error' : ''}`}
            placeholder="Enter product title"
            maxLength={500}
          />
          {validationErrors.title && (
            <span className="errorMessage">{validationErrors.title}</span>
          )}
        </div>

        <div className="formField fullWidth">
          <label className="formLabel">
            Description <span className="required">*</span>
          </label>
          <HTMLEditor
            value={formData.description}
            onChange={(value) => handleInputChange('description', value)}
            placeholder="Enter a detailed product description with rich formatting (required)"
          />
          {validationErrors.description && (
            <span className="errorMessage">{validationErrors.description}</span>
          )}
        </div>

      </div>
    </div>
  );

  const renderPricingInventory = () => (
    <div className="shopifyCard addProductSectionCard">
      <div className="shopifyCardHeader">
        <h3>Price & Inventory</h3>
      </div>
      <div className="formGrid">
        <div className="formField">
          <label className="formLabel">
            Price <span className="required">*</span>
          </label>
          <div className="inputWithPrefix">
            <span className="inputPrefix">₹</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              onBlur={() => handleBlur('price')}
              className={`formInput withPrefix ${validationErrors.price ? 'error' : ''}`}
              placeholder="0.00"
            />
          </div>
          {validationErrors.price && (
            <span className="errorMessage">{validationErrors.price}</span>
          )}
        </div>

        <div className="formField">
          <label className="formLabel">
            Compare At Price
          </label>
          <div className="inputWithPrefix">
            <span className="inputPrefix">₹</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.compare_price}
              onChange={(e) => handleInputChange('compare_price', e.target.value)}
              onBlur={() => handleBlur('compare_price')}
              className={`formInput withPrefix ${validationErrors.compare_price ? 'error' : ''}`}
              placeholder="0.00"
            />
          </div>
          {validationErrors.compare_price && (
            <span className="errorMessage">{validationErrors.compare_price}</span>
          )}
        </div>

        <div className="formField">
          <label className="formLabel">
            Inventory Quantity <span className="required">*</span>
          </label>
          <input
            type="number"
            min="0"
            step="1"
            value={formData.inventory_quantity}
            onChange={(e) => handleInputChange('inventory_quantity', e.target.value)}
            onBlur={() => handleBlur('inventory_quantity')}
            className={`formInput ${validationErrors.inventory_quantity ? 'error' : ''}`}
            placeholder="0"
          />
          {validationErrors.inventory_quantity && (
            <span className="errorMessage">{validationErrors.inventory_quantity}</span>
          )}
        </div>

        <div className="formField">
          <label className="formLabel">
            Commission (%)
          </label>
          <div className="inputWithSuffix">
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={formData.commission_pct}
              onChange={(e) => handleInputChange('commission_pct', e.target.value)}
              onBlur={() => handleBlur('commission_pct')}
              className={`formInput withSuffix ${validationErrors.commission_pct ? 'error' : ''}`}
              placeholder="0"
            />
            <span className="inputSuffix">%</span>
          </div>
          {validationErrors.commission_pct && (
            <span className="errorMessage">{validationErrors.commission_pct}</span>
          )}
        </div>

        <div className="formField">
          <label className="formLabel">
            GST Rate (%) <span className="required">*</span>
          </label>
          <div className="inputWithSuffix">
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.gst_rate}
              onChange={(e) => handleInputChange('gst_rate', e.target.value)}
              onBlur={() => handleBlur('gst_rate')}
              className={`formInput withSuffix ${validationErrors.gst_rate ? 'error' : ''}`}
              placeholder="0"
            />
            <span className="inputSuffix">%</span>
          </div>
          {validationErrors.gst_rate && (
            <span className="errorMessage">{validationErrors.gst_rate}</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderShippingDetails = () => (
    <div className="shopifyCard addProductSectionCard">
      <div className="shopifyCardHeader">
        <h3>Shipping</h3>
      </div>
      <div className="formGrid">
        <div className="formField">
          <label className="formLabel">
            Weight
          </label>
          <div className="inputWithSuffix">
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.weight_kg}
              onChange={(e) => handleInputChange('weight_kg', e.target.value)}
              onBlur={() => handleBlur('weight_kg')}
              className={`formInput withSuffix ${validationErrors.weight_kg ? 'error' : ''}`}
              placeholder="0.00"
            />
            <span className="inputSuffix">kg</span>
          </div>
          {validationErrors.weight_kg && (
            <span className="errorMessage">{validationErrors.weight_kg}</span>
          )}
        </div>

        <div className="formField">
          <label className="formLabel">
            Length
          </label>
          <div className="inputWithSuffix">
            <input
              type="number"
              step="0.1"
              min="0"
              value={formData.length_cm}
              onChange={(e) => handleInputChange('length_cm', e.target.value)}
              onBlur={() => handleBlur('length_cm')}
              className={`formInput withSuffix ${validationErrors.length_cm ? 'error' : ''}`}
              placeholder="0.0"
            />
            <span className="inputSuffix">cm</span>
          </div>
          {validationErrors.length_cm && (
            <span className="errorMessage">{validationErrors.length_cm}</span>
          )}
        </div>

        <div className="formField">
          <label className="formLabel">
            Width
          </label>
          <div className="inputWithSuffix">
            <input
              type="number"
              step="0.1"
              min="0"
              value={formData.width_cm}
              onChange={(e) => handleInputChange('width_cm', e.target.value)}
              onBlur={() => handleBlur('width_cm')}
              className={`formInput withSuffix ${validationErrors.width_cm ? 'error' : ''}`}
              placeholder="0.0"
            />
            <span className="inputSuffix">cm</span>
          </div>
          {validationErrors.width_cm && (
            <span className="errorMessage">{validationErrors.width_cm}</span>
          )}
        </div>

        <div className="formField">
          <label className="formLabel">
            Height
          </label>
          <div className="inputWithSuffix">
            <input
              type="number"
              step="0.1"
              min="0"
              value={formData.height_cm}
              onChange={(e) => handleInputChange('height_cm', e.target.value)}
              onBlur={() => handleBlur('height_cm')}
              className={`formInput withSuffix ${validationErrors.height_cm ? 'error' : ''}`}
              placeholder="0.0"
            />
            <span className="inputSuffix">cm</span>
          </div>
          {validationErrors.height_cm && (
            <span className="errorMessage">{validationErrors.height_cm}</span>
          )}
        </div>

        {(!hasVariants || sameHsCodeAcrossVariants) && (
          <div className="formField fullWidth">
            <label className="formLabel">
              HS Code{hasVariants && sameHsCodeAcrossVariants && ' (applies to all variants)'}
            </label>
            <input
              type="text"
              value={formData.hs_code}
              onChange={(e) => {
                // Only allow numeric input
                const value = e.target.value.replace(/\D/g, '');
                handleInputChange('hs_code', value);
                // If toggle is on, update all existing variants with the new HS code
                if (hasVariants && sameHsCodeAcrossVariants && variants.length > 0) {
                  setVariants(prev => prev.map(v => ({ ...v, hs_code: value })));
                }
              }}
              className={`formInput ${validationErrors.hs_code ? 'error' : ''}`}
              placeholder="6109 or 61091000"
              maxLength={8}
            />
            <div className="helperText">
              Enter 4 to 8 digit HSN/SAC code (e.g. 6109, 61091000)
            </div>
            {validationErrors.hs_code && (
              <span className="errorMessage">{validationErrors.hs_code}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderMediaMeta = () => (
    <div className="formSection">
      <h3 className="sectionTitle">Media</h3>
      <div className="formGrid">
        <div className="formField fullWidth">
          <label className="formLabel">
            Product Images <span className="required">*</span>
          </label>
          <div className="imageUploadContainer">
            <input
              type="file"
              id="imageUpload"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              multiple
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
            <p className="helpText">Accepts: JPG, PNG, GIF, WebP (Max 25MB each)</p>

            {selectedUploadedImageIndexes.length > 0 && (
              <button
                type="button"
                className="bulkActionButton danger imageBulkDeleteButton"
                onClick={handleBulkRemoveUploadedImages}
                disabled={uploadingImages}
              >
                Delete Selected ({selectedUploadedImageIndexes.length})
              </button>
            )}
            
            {imageUploadError && (
              <div className="errorMessage">{imageUploadError}</div>
            )}
            
            {validationErrors.images && (
              <span className="errorMessage">{validationErrors.images}</span>
            )}

            <div className="uploadedImagesGrid">
              {uploadedImages.map((image, index) => {
                const isSelected = selectedUploadedImageIndexes.includes(index);
                return (
                  <div
                    key={`${image.url}-${index}`}
                    className={`uploadedImageTile ${isSelected ? 'selected' : ''}`}
                    draggable
                    onDragStart={() => setDraggedUploadedImageIndex(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      reorderUploadedImages(draggedUploadedImageIndex, index);
                      setDraggedUploadedImageIndex(null);
                    }}
                    onDragEnd={() => setDraggedUploadedImageIndex(null)}
                  >
                    <span className="uploadedImageDragHandle" title="Drag to reorder" aria-hidden="true">⠿</span>
                    <label className="uploadedImageCheckbox" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleUploadedImageSelection(index)}
                      />
                    </label>
                    <img src={image.url} alt={`uploaded-${index + 1}`} className="uploadedImageTileImg" />
                  </div>
                );
              })}
              <button
                type="button"
                className="uploadedImageAddTile"
                onClick={() => {
                  if (!uploadingImages) {
                    const inputEl = document.getElementById('imageUpload');
                    if (inputEl) inputEl.click();
                  }
                }}
                disabled={uploadingImages}
              >
                <span className="uploadedImageAddIcon">{uploadingImages ? '…' : '+'}</span>
              </button>
            </div>
          </div>
        </div>
        <div className="formField fullWidth">
          <label className="formLabel">Category <span className="required">*</span></label>
          <div className="searchableDropdown" ref={categoryDropdownRef}>
            <input
              type="text"
              value={categorySearchTerm}
              onChange={handleCategorySearchChange}
              onFocus={() => setShowCategoryDropdown(true)}
              placeholder="Choose a product category"
              className={`formInput searchInputNarrow ${validationErrors.category_id ? 'error' : ''}`}
            />
            {showCategoryDropdown && (
              <div className="dropdownMenu">
                {categoryLoading ? (
                  <div className="dropdownItem dropdownItemDisabled">Searching...</div>
                ) : categorySuggestions.length > 0 ? (
                  categorySuggestions.map(category => (
                    <div key={category.id} className="dropdownItem" onClick={() => handleCategorySelect(category)}>
                      {highlightMatch(category.name, categorySearchTerm)}
                    </div>
                  ))
                ) : (
                  <div className="dropdownItem dropdownItemDisabled">Type at least 2 characters to search</div>
                )}
              </div>
            )}
          </div>
          <p className="helpText">Determines tax rates and adds metafields to improve search, filters, and cross-channel sales</p>
          {categoriesError && <span className="errorMessage">{categoriesError}</span>}
          {validationErrors.category_id && <span className="errorMessage">{validationErrors.category_id}</span>}
        </div>

      </div>
    </div>
  );

  const renderShopifySidebar = () => (
    <div className="sideColumn">
      <div className="shopifyCard">
        <div className="shopifyCardHeader"><h3>Status</h3></div>
        <select className="shopifySelect" value={productStatus} onChange={(e) => setProductStatus(e.target.value)}>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
        </select>
      </div>
      <div className="shopifyCard">
        <div className="shopifyCardHeader"><h3>Product organization</h3></div>
        {userRole === 'admin' && (
          <div className="formField">
            <label className="formLabel">Vendor <span className="required">*</span></label>
            <div className="searchableDropdown" ref={vendorDropdownRef}>
              <input
                type="text"
                value={vendorSearchTerm}
                onChange={handleVendorSearchChange}
                onFocus={() => setShowVendorDropdown(true)}
                placeholder="None"
                className={`formInput searchInputNarrow ${validationErrors.vendor_id ? 'error' : ''}`}
              />
              {showVendorDropdown && (
                <div className="dropdownMenu">
                  {vendorLoading ? (
                    <div className="dropdownItem dropdownItemDisabled">Searching...</div>
                  ) : vendorSuggestions.length > 0 ? (
                    vendorSuggestions.map(vendor => (
                      <div key={vendor.id} className="dropdownItem" onClick={() => handleVendorSelect(vendor)}>
                        {highlightMatch(vendor.brand_name || vendor.company_name, vendorSearchTerm)}
                      </div>
                    ))
                  ) : (
                    <div className="dropdownItem dropdownItemDisabled">Type at least 2 characters to search</div>
                  )}
                </div>
              )}
            </div>
            {validationErrors.vendor_id && <span className="errorMessage">{validationErrors.vendor_id}</span>}
          </div>
        )}
        <div className="formField">
          <label className="formLabel">Tags</label>
          <div className="tagsInput">
            <div className="tagsDisplay">
              {(formData.tags || []).map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                  <button type="button" onClick={() => handleRemoveTag(index)} className="tagRemove">×</button>
                </span>
              ))}
            </div>
            <input
              type="text"
              placeholder="Add tags"
              className="formInput"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  handleAddTag(e.target.value.trim());
                  e.target.value = '';
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render options section
  const renderOptionsSection = () => (
    <div className="formSection">
      <h3 className="sectionTitle" style={{ marginBottom: '1rem' }}>Variants</h3>
      <div>
        <div className="shopifyVariantsOptionsContainer">
          {selectedOptions.map(optionId => {
            const option = variantsOptions.find(o => o.id === optionId);
            const optionName = optionDisplayNames[optionId] || option?.name || 'Option';
            const rawOptionValuesList = optionValues[optionId] || [];
            const chipOnlyValues = rawOptionValuesList.filter((v) => String(v).trim() !== '');
            const isExpanded = expandedOptionEditors[optionId] !== false;
            const valueRowIds = optionValueRowIds[optionId] || [];
            const cardDragType = 'application/x-manual-variant-option-card';
            const valueDragType = 'application/x-manual-variant-option-value';

            return (
              <div
                key={optionId}
                className={[
                  'shopifyOptionCard',
                  !isExpanded ? 'shopifyOptionCard--collapsed' : '',
                  draggingOptionCardId === optionId ? 'shopifyOptionCard--dragging' : '',
                  dragOverOptionId === optionId ? 'shopifyOptionCard--dropTarget' : '',
                ].filter(Boolean).join(' ')}
                onDragOver={(e) => {
                  if (![...e.dataTransfer.types].includes(cardDragType)) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDragOverOptionId(optionId);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setDragOverOptionId((id) => (id === optionId ? null : id));
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const sourceId = e.dataTransfer.getData(cardDragType);
                  if (sourceId) {
                    reorderVariantOptionCards(sourceId, optionId);
                  }
                  setDraggingOptionCardId(null);
                  setDragOverOptionId(null);
                }}
              >
                {isExpanded ? (
                  <>
                    <div className="shopifyOptionCardTopBar">
                      <span
                        className="shopifyOptionGrip shopifyOptionCardDragGrip"
                        draggable
                        onDragStart={(e) => {
                          e.stopPropagation();
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData(cardDragType, optionId);
                          setDraggingOptionCardId(optionId);
                        }}
                        onDragEnd={() => {
                          setDraggingOptionCardId(null);
                          setDragOverOptionId(null);
                        }}
                        title="Drag to reorder options"
                        aria-hidden
                      >
                        ⋮⋮
                      </span>
                    </div>
                    <div className="shopifyOptionHeader">
                      <label className="formLabel">Option name</label>
                      <input
                        type="text"
                        className="formInput"
                        value={optionName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setOptionDisplayNames((prev) => ({ ...prev, [optionId]: value }));
                          if (groupByOption === (option?.name || optionDisplayNames[optionId])) {
                            setGroupByOption(value);
                          }
                        }}
                      />
                    </div>

                    <div className="formField">
                      <label className="formLabel">Option values</label>
                      {rawOptionValuesList.map((value, index) => {
                        const dropKey = `${optionId}:${index}`;
                        const rowKey = valueRowIds[index] || `${optionId}-value-${index}`;
                        return (
                          <div
                            key={rowKey}
                            className={[
                              'shopifyOptionValueRow',
                              dragOverValueDropKey === dropKey ? 'shopifyOptionValueRow--dropTarget' : '',
                            ].filter(Boolean).join(' ')}
                            onDragOver={(e) => {
                              if (![...e.dataTransfer.types].includes(valueDragType)) return;
                              e.preventDefault();
                              e.stopPropagation();
                              e.dataTransfer.dropEffect = 'move';
                              setDragOverValueDropKey(dropKey);
                            }}
                            onDragLeave={(e) => {
                              if (!e.currentTarget.contains(e.relatedTarget)) {
                                setDragOverValueDropKey((k) => (k === dropKey ? null : k));
                              }
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const raw = e.dataTransfer.getData(valueDragType);
                              let payload = null;
                              try {
                                payload = raw ? JSON.parse(raw) : null;
                              } catch {
                                payload = null;
                              }
                              if (!payload || payload.optionId !== optionId) {
                                setDragOverValueDropKey(null);
                                return;
                              }
                              reorderVariantOptionValues(optionId, payload.index, index);
                              setDragOverValueDropKey(null);
                            }}
                          >
                            <span
                              className="shopifyOptionGrip shopifyOptionValueDragGrip"
                              draggable
                              onDragStart={(e) => {
                                e.stopPropagation();
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData(
                                  valueDragType,
                                  JSON.stringify({ optionId, index })
                                );
                              }}
                              onDragEnd={() => setDragOverValueDropKey(null)}
                              title="Drag to reorder values"
                              aria-hidden
                            >
                              ⋮⋮
                            </span>
                            <input
                              type="text"
                              className="formInput"
                              value={value}
                              onChange={(e) => handleOptionValueInputChange(optionId, index, e.target.value)}
                              onBlur={(e) => handleCommittedOptionValueBlur(optionId, index, e.target.value)}
                            />
                            <button
                              type="button"
                              className="shopifyOptionTrash"
                              onClick={() => handleRemoveOptionValue(optionId, index)}
                              title="Remove value"
                            >
                              🗑
                            </button>
                          </div>
                        );
                      })}
                      <div className="shopifyOptionValueRow shopifyOptionAddRow">
                        <span className="shopifyOptionGrip shopifyOptionGrip--static">⋮⋮</span>
                        <input
                          type="text"
                          value={currentOptionValue[optionId] || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCurrentOptionValue((prev) => ({ ...prev, [optionId]: v }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddOptionValue(optionId, currentOptionValue[optionId]);
                            }
                          }}
                          onBlur={() => handleAddOptionValue(optionId, currentOptionValue[optionId])}
                          placeholder="Add another value"
                          className="formInput"
                        />
                        <span />
                      </div>
                    </div>

                    <div className="shopifyOptionActionsRow">
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(optionId)}
                        className="shopifyOptionNeutralBtn shopifyOptionNeutralBtn--danger"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        className="shopifyOptionNeutralBtn shopifyOptionNeutralBtn--primary"
                        onClick={() => setExpandedOptionEditors((prev) => ({ ...prev, [optionId]: false }))}
                      >
                        Done
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="shopifyOptionCollapsedRow">
                    <span
                      className="shopifyOptionGrip shopifyOptionCardDragGrip"
                      draggable
                      onDragStart={(e) => {
                        e.stopPropagation();
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData(cardDragType, optionId);
                        setDraggingOptionCardId(optionId);
                      }}
                      onDragEnd={() => {
                        setDraggingOptionCardId(null);
                        setDragOverOptionId(null);
                      }}
                      title="Drag to reorder options"
                      aria-hidden
                    >
                      ⋮⋮
                    </span>
                    <div
                      className="shopifyOptionCollapsedView"
                      role="button"
                      tabIndex={0}
                      onClick={() => setExpandedOptionEditors((prev) => ({ ...prev, [optionId]: true }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setExpandedOptionEditors((prev) => ({ ...prev, [optionId]: true }));
                        }
                      }}
                    >
                      <div className="shopifyOptionCollapsedName">{optionName}</div>
                      <div className="shopifyOptionCollapsedValues">
                        {chipOnlyValues.map((value, index) => (
                          <span key={`${optionId}-chip-${index}`} className="tag">{value}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ marginTop: '0.75rem' }}>
        {selectedOptions.length < 3 && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <OptionNameCombobox
              options={variantsOptions}
              excludeIds={selectedOptions}
              onSelectExisting={(opt) => handleAddOption(opt.id)}
              onCreate={(opt) => handleAddCreatedOption(opt)}
            />
          </div>
        )}
      </div>
    </div>
  );

  // Render variants table
  const renderVariantsTable = () => {
    const selectedGroupOptionId = selectedOptions.find((optionId) => {
      const baseName = variantsOptions.find((opt) => opt.id === optionId)?.name || '';
      const displayName = optionDisplayNames[optionId] || baseName;
      return displayName === groupByOption;
    });

    const groupOrderValues = selectedGroupOptionId
      ? (optionValues[selectedGroupOptionId] || []).filter((value) => value.trim() !== '')
      : [];

    const groupOrderIndex = new Map(groupOrderValues.map((value, index) => [value, index]));

    const displayedVariants = variants
      .map((variant, originalIndex) => ({
        variant,
        originalIndex,
        groupLabel: groupByOption ? (getOptionValue(variant, groupByOption) || 'Other') : null,
      }))
      .sort((a, b) => {
        if (!groupByOption) return a.originalIndex - b.originalIndex;

        const aGroup = a.groupLabel || 'Other';
        const bGroup = b.groupLabel || 'Other';
        const aOrder = groupOrderIndex.has(aGroup) ? groupOrderIndex.get(aGroup) : Number.MAX_SAFE_INTEGER;
        const bOrder = groupOrderIndex.has(bGroup) ? groupOrderIndex.get(bGroup) : Number.MAX_SAFE_INTEGER;

        if (aOrder !== bOrder) return aOrder - bOrder;
        if (aGroup !== bGroup) return aGroup.localeCompare(bGroup);

        const aName = a.variant.name || '';
        const bName = b.variant.name || '';
        if (aName !== bName) return aName.localeCompare(bName);
        return a.originalIndex - b.originalIndex;
      });

    const groupCounts = displayedVariants.reduce((acc, item) => {
      if (!item.groupLabel) return acc;
      acc[item.groupLabel] = (acc[item.groupLabel] || 0) + 1;
      return acc;
    }, {});

    const totalVariants = variants.length;
    let variantsSelectedCount = 0;
    for (let i = 0; i < totalVariants; i++) {
      if (selectedVariantIndexes.has(i)) variantsSelectedCount++;
    }
    const variantsAllSelected =
      totalVariants > 0 && variantsSelectedCount === totalVariants;
    const variantsPartiallySelected =
      variantsSelectedCount > 0 && variantsSelectedCount < totalVariants;

    return (
      <div className="formSection generatedVariantsSection">
        <div className="generatedVariantsToolbar">
          <h3 className="sectionTitle generatedVariantsTitle">Generated Variants ({variants.length})</h3>
          {variants.length > 0 && (
            <div className="generatedVariantsGroupBy">
              <label className="formLabel" htmlFor="variantGroupBySelect">Group by</label>
              <select
                id="variantGroupBySelect"
                className="formInput"
                value={groupByOption}
                onChange={(e) => setGroupByOption(e.target.value)}
              >
                {selectedOptions.map((optionId) => {
                  const option = variantsOptions.find((opt) => opt.id === optionId);
                  if (!option) return null;
                  const displayName = optionDisplayNames[optionId] || option.name;
                  return (
                    <option key={option.id} value={displayName}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>

        {variants.length === 0 ? (
          <div className="generatedVariantsEmptyPlaceholder">
            Add options and values above to generate variants
          </div>
        ) : (
          <div className="tableWrapper variantsTableWrapper">
            <table className="productTable variantsCompactTable" style={{ width: '100%' }}>
              <colgroup>
                <col style={{ width: '5%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '31%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '16%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="checkboxColumn" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="variantsCompactHeaderTriCheckbox"
                      role="checkbox"
                      aria-checked={variantsPartiallySelected ? 'mixed' : variantsAllSelected}
                      aria-label={
                        variantsAllSelected ? 'Deselect all variants' : 'Select all variants'
                      }
                      disabled={totalVariants === 0}
                      onClick={handleVariantsHeaderSelectionClick}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleVariantsHeaderSelectionClick(e);
                        }
                      }}
                    >
                      <span className="variantsCompactHeaderTriCheckbox-box" aria-hidden>
                        {variantsAllSelected ? (
                          <span className="variantsCompactHeaderTriCheckbox-check">✓</span>
                        ) : variantsPartiallySelected ? (
                          <span className="variantsCompactHeaderTriCheckbox-dash">−</span>
                        ) : null}
                      </span>
                    </button>
                  </th>
                  <th>Image</th>
                  <th>Variant</th>
                  <th>SKU</th>
                  <th>Price</th>
                  <th>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {displayedVariants.map((entry, displayIndex) => {
                  const { variant, originalIndex, groupLabel } = entry;
                  const imageUrl = variant.image_urls ? variant.image_urls.split('|')[0] : null;
                  const variantError = variantErrors[originalIndex];
                  const previousGroupLabel = displayIndex > 0 ? displayedVariants[displayIndex - 1].groupLabel : null;
                  const shouldRenderGroupHeader = Boolean(groupLabel && groupLabel !== previousGroupLabel);
                  const isGroupExpanded = groupLabel ? expandedGroups[groupLabel] !== false : true;
                  const isChildRow = groupLabel && !shouldRenderGroupHeader;
                  if (groupLabel && !isGroupExpanded && !shouldRenderGroupHeader) {
                    return null;
                  }
                  return (
                    <React.Fragment key={originalIndex}>
                      {shouldRenderGroupHeader && (
                        <tr>
                          <td colSpan={6} style={{ background: '#f7f7f7', fontWeight: 600, cursor: 'pointer' }} onClick={() => setExpandedGroups((prev) => ({ ...prev, [groupLabel]: prev[groupLabel] === false }))}>
                            {groupLabel} ({groupCounts[groupLabel] || 0} variants)
                          </td>
                        </tr>
                      )}
                    <tr
                      className="variantsCompactRow"
                      style={variantError ? { backgroundColor: '#fff5f5' } : {}}
                    >
                      <td className="checkboxColumn" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedVariantIndexes.has(originalIndex)}
                          onChange={() => toggleVariantSelection(originalIndex)}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          type="button"
                          className="variantImagePickerButton"
                          onClick={(e) => {
                            e.stopPropagation();
                            openVariantImagePicker(originalIndex);
                          }}
                          title="Select image"
                        >
                          {imageUrl ? (
                            <img src={imageUrl} alt="Variant" className="variantImagePreview" />
                          ) : (
                            <span className="variantImagePickerIcon">＋</span>
                          )}
                        </button>
                      </td>
                      <td style={{ paddingLeft: isChildRow ? '24px' : '10px' }}>
                        <button
                          type="button"
                          className="variantsReadOnlyName variantsNameLinkButton"
                          title={variant.name || ''}
                          onClick={(e) => {
                            e.stopPropagation();
                            openVariantDetailsModal(originalIndex);
                          }}
                        >
                          {variant.name || '-'}
                        </button>
                      </td>
                      <td>
                        <input
                          className={`formInput variantsCompactInput ${variantErrors[originalIndex]?.sku ? 'error' : ''}`}
                          value={variant.sku || ''}
                          onChange={(e) => handleInlineVariantChange(originalIndex, 'sku', e.target.value.toUpperCase())}
                          onClick={(e) => e.stopPropagation()}
                        />
                        {variantErrors[originalIndex]?.sku && (
                          <div className="variantErrorMessage">{variantErrors[originalIndex].sku}</div>
                        )}
                      </td>
                      <td>
                        <div className="inputWithPrefix">
                          <span className="inputPrefix">₹</span>
                          <input
                            type="number"
                            className={`formInput withPrefix variantsCompactInput variantsPriceInput ${variantErrors[originalIndex]?.price ? 'error' : ''}`}
                            value={variant.price || ''}
                            onChange={(e) => handleInlineVariantChange(originalIndex, 'price', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          {variantErrors[originalIndex]?.price && (
                            <div className="variantErrorMessage">{variantErrors[originalIndex].price}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="formInput variantsCompactInput variantsQtyInput"
                          min="0"
                          value={variant.inventory_quantity || 0}
                          onChange={(e) => handleInlineVariantChange(originalIndex, 'inventory_quantity', e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="manualAddProductContainer">
      {showVariantImagePicker && (
        <div className="imagePickerModal">
          <div className="imagePickerContent" style={{ maxWidth: '980px' }}>
            <div className="imagePickerHeader">
              <h3>Select image</h3>
              <button
                type="button"
                onClick={() => {
                  setShowVariantImagePicker(false);
                  setVariantImagePickerIndex(null);
                }}
                className="closeButton"
              >
                ×
              </button>
            </div>
            <div className="imagePickerBody">
              <div className="imagePickerGrid">
                {uploadedImages.map((item, idx) => (
                  <button
                    key={`${item.url}-${idx}`}
                    type="button"
                    className="imagePickerItem"
                    onClick={() => handleSelectVariantImage(item.url)}
                  >
                    <img src={item.url} alt={item.fileName || `image-${idx + 1}`} />
                  </button>
                ))}
              </div>
            </div>
            <div className="imagePickerFooter">
              <button
                type="button"
                className="bulkActionButton"
                onClick={() => {
                  setShowVariantImagePicker(false);
                  setVariantImagePickerIndex(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showVariantPopup && variantPopupDraft && (
        <div className="imagePickerModal">
          <div className="imagePickerContent" style={{ maxWidth: '760px' }}>
            <div className="imagePickerHeader">
              <h3>Edit Variant Details</h3>
              <button type="button" onClick={handleCloseVariantEditor} className="closeButton">×</button>
            </div>
            <div className="imagePickerBody" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="formField"><label className="formLabel">Variant Name</label><input className="formInput" value={variantPopupDraft.name || ''} readOnly /></div>
              <div className="formField"><label className="formLabel">SKU</label><input className="formInput" value={variantPopupDraft.sku || ''} onChange={(e) => setVariantPopupDraft((prev) => ({ ...prev, sku: e.target.value.toUpperCase() }))} /></div>
              <div className="formField"><label className="formLabel">Price</label><input type="number" className="formInput" value={variantPopupDraft.price || ''} onChange={(e) => setVariantPopupDraft((prev) => ({ ...prev, price: e.target.value }))} /></div>
              <div className="formField"><label className="formLabel">Inventory</label><input type="number" min="0" className="formInput" value={variantPopupDraft.inventory_quantity || ''} onChange={(e) => setVariantPopupDraft((prev) => ({ ...prev, inventory_quantity: e.target.value }))} /></div>
              <div className="formField"><label className="formLabel">Compare-at Price</label><input type="number" className="formInput" value={variantPopupDraft.compare_price || ''} onChange={(e) => setVariantPopupDraft((prev) => ({ ...prev, compare_price: e.target.value }))} /></div>
              <div className="formField"><label className="formLabel">HSN</label>
                <input 
                  className="formInput" 
                  value={variantPopupDraft.hs_code || ''} 
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setVariantPopupDraft((prev) => ({ ...prev, hs_code: value }));
                  }}
                  placeholder="6109 or 61091000"
                  maxLength={8}
                />
                <div className="helperText">
                  Enter 4 to 8 digit HSN/SAC code (e.g. 6109, 61091000)
                </div>
              </div>
              <div className="formField"><label className="formLabel">GST (%)</label><input type="number" className="formInput" value={variantPopupDraft.gst_rate || ''} onChange={(e) => setVariantPopupDraft((prev) => ({ ...prev, gst_rate: e.target.value }))} /></div>
              <div className="formField"><label className="formLabel">Weight (kg)</label><input type="number" className="formInput" value={variantPopupDraft.weight_kg || ''} onChange={(e) => setVariantPopupDraft((prev) => ({ ...prev, weight_kg: e.target.value }))} /></div>
              <div className="formField"><label className="formLabel">Length (cm)</label><input type="number" className="formInput" value={variantPopupDraft.length_cm || ''} onChange={(e) => setVariantPopupDraft((prev) => ({ ...prev, length_cm: e.target.value }))} /></div>
              <div className="formField"><label className="formLabel">Width (cm)</label><input type="number" className="formInput" value={variantPopupDraft.width_cm || ''} onChange={(e) => setVariantPopupDraft((prev) => ({ ...prev, width_cm: e.target.value }))} /></div>
              <div className="formField"><label className="formLabel">Height (cm)</label><input type="number" className="formInput" value={variantPopupDraft.height_cm || ''} onChange={(e) => setVariantPopupDraft((prev) => ({ ...prev, height_cm: e.target.value }))} /></div>
            </div>
            <div className="imagePickerFooter">
              <button type="button" className="bulkActionButton" onClick={handleCloseVariantEditor}>Cancel</button>
              <button type="button" className="bulkActionButton primary" onClick={handleVariantSave}>Save</button>
            </div>
          </div>
        </div>
      )}

      <div className="header">
        <h1 className="title">Add New Product</h1>
        <div className="headerInfo">
          <span className="infoBadge">Manual Entry</span>
        </div>
      </div>

      {/* Status Messages */}
      {saveStatus && (
        <div className={`statusMessage ${saveStatus.type}`}>
          <span className="statusIcon">
            {saveStatus.type === 'success' ? '✓' : '⚠'}
          </span>
          <span className="statusText">{saveStatus.message}</span>
        </div>
      )}

      <div className="shopifyLayout">
        <div className="mainColumn">
          {renderBasicInfo()}
          {renderMediaMeta()}
        </div>
        {renderShopifySidebar()}
      </div>

      {/* Form Sections */}
      <div className="shopifyLayout addProductSectionsLayout">
        <div className="mainColumn">
          {!hasVariants && (
            <div className="shopifyCard addProductSectionCard">
              <div className="formField">
                <label className="formLabel">SKU <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => handleInputChange('sku', e.target.value.toUpperCase())}
                  onBlur={() => handleBlur('sku')}
                  className={`formInput ${validationErrors.sku ? 'error' : ''}`}
                  placeholder="PROD-001"
                />
                {validationErrors.sku && <span className="errorMessage">{validationErrors.sku}</span>}
              </div>
            </div>
          )}
          {renderPricingInventory()}
          {renderShippingDetails()}
          {renderOptionsSection()}
          {hasVariants && sameHsCodeAcrossVariants && (
            <div className="shopifyCard addProductSectionCard">
              <label className="formLabel">HS Code (applies to all variants)</label>
              <input
                type="text"
                value={formData.hs_code}
                onChange={(e) => {
                  handleInputChange('hs_code', e.target.value);
                  if (variants.length > 0) {
                    setVariants(prev => prev.map(v => ({ ...v, hs_code: e.target.value })));
                  }
                }}
                className="formInput"
                placeholder="e.g., 8471.30.00"
              />
            </div>
          )}
          {hasVariants && renderVariantsTable()}
        </div>
        <div className="sideColumn addProductSectionsSidebarSpacer" />
      </div>

      {/* Action Buttons */}
      <div className="addProductActions">
        <button
          onClick={handleCancel}
          className="cancelButton"
          disabled={isSaving}
        >
          Cancel
        </button>

        <div className="primaryActions">
          <button
            onClick={handleSaveDraft}
            className="saveDraftButton"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>

          <button
            onClick={handleSubmitForApproval}
            className="submitApprovalButton"
            disabled={isSaving || Object.keys(validationErrors).length > 0}
            title={Object.keys(validationErrors).length > 0 ? 'Please fix all validation errors first' : ''}
          >
            {isSaving ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ManualAddProductPage;